'use client';

import { useState, useRef, useEffect, Fragment } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import toast from 'react-hot-toast';
import { timeAgo, formatNumber } from '@/lib/utils/format';
import { useI18n } from '@/lib/i18n/context';
import { AdSlot } from './AdSlot';
import { useAdBlock } from '@/contexts/AdBlockContext';
import { cn } from '@/lib/utils/cn';
import { usePostTranslate, useFeedTranslate } from '@/lib/hooks/useTranslate';
import { frontPageStrings } from '@/locales/frontpage';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Comment {
  id: string; content: string; parentId: string | null; createdAt: string;
  replyCount: number; author: { name: string; avatar: string | null };
}

interface Author {
  id: string; name: string; username: string | null; avatar: string | null;
  postCount: number; followerCount: number;
}

interface RelatedItem {
  id: string; src: string; thumb?: string | null; title: string;
}

interface Props {
  post: {
    id: string; title: string; description: string | null; hashtags: string | null;
    views: number; likes: number; shares: number; commentCount: number;
    createdAt: string; isLiked: boolean; isSaved: boolean; keywords: string[] | null;
  };
  medias: { id: string; src: string | null; thumb: string | null; caption: string | null }[];
}

// ─── Skeleton helpers ─────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-surface-700/60 rounded-xl ${className ?? ''}`} />
  );
}

function AuthorSkeleton() {
  return (
    <div className="bg-surface-900 rounded-2xl p-5 mb-6 border border-white/5">
      <div className="flex items-center gap-4">
        <Skeleton className="w-14 h-14 rounded-2xl flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="mt-4 h-10 w-full rounded-2xl" />
    </div>
  );
}

function CommentSkeleton() {
  return (
    <div className="flex gap-3">
      <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  );
}

function RelatedSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="rounded-2xl aspect-[3/4] w-full" />
      ))}
    </>
  );
}

function RelatedCard({ id, src, thumb, title }: RelatedItem) {
  const [imgError, setImgError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  if (imgError) return <div className="hidden" />;

  return (
    <Link
      href={`/p/${id}`}
      className="group relative rounded-2xl overflow-hidden bg-surface-800 hover:scale-[1.02] transition-transform duration-300"
    >
      {/* Skeleton */}
      {!isLoaded && (
        <div className="w-full aspect-[3/4] bg-surface-700 animate-pulse" />
      )}

      {/* Image luôn có mặt trong DOM — loading="lazy" (native) vẫn trì hoãn
          tải ảnh thật cho trình duyệt, nhưng thẻ <img> vẫn đọc được bởi crawler */}
      <Image
        src={thumb || src}
        alt={title}
        width={300}
        height={400}
        loading="lazy"
        className={cn(
          'w-full h-auto transition-opacity duration-500',
          isLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
        )}
        onLoad={() => setIsLoaded(true)}
        onError={() => setImgError(true)}
      />

      {/* Overlay — chỉ hiện sau khi ảnh load xong */}
      {isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-40 group-hover:opacity-100 transition-opacity p-3 flex items-end">
          <p className="text-white text-xs font-medium line-clamp-2">{title}</p>
        </div>
      )}
    </Link>
  );
}
// ─── Main component ────────────────────────────────────────────────────────────

export function PostDetail({ post, medias: rawMedias }: Props) {
  const { data: session } = useSession();
  const router = useRouter();
  const { t, locale } = useI18n();
  const { hasAdBlock, checked } = useAdBlock();
  const degraded = hasAdBlock && checked;
  const { translatedTitle, translatedDesc, isTranslating, isTranslated, detectedLang } = usePostTranslate(
    post.id, { title: post.title, description: post.description }, locale
  );
  const [showOriginal, setShowOriginal] = useState(false);
  const viewOriginal = isTranslated && showOriginal;
  const fp = frontPageStrings[locale];
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!shareMenuOpen) return;
    const onDown = (e: MouseEvent) => { if (!shareRef.current?.contains(e.target as Node)) setShareMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShareMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [shareMenuOpen]);

  // ── Filter medias by image width >= 600px ────────────────────────────────────
  const [medias, setMedias] = useState(rawMedias);

  useEffect(() => {
    let cancelled = false;

    const checkWidths = async () => {
      const results = await Promise.all(
        rawMedias.map(
          m =>
            new Promise<{ media: typeof m; width: number }>(resolve => {
              const url = m.src || m.thumb;
              if (!url) return resolve({ media: m, width: 0 });
              const img = new window.Image();
              img.onload = () => resolve({ media: m, width: img.naturalWidth });
              img.onerror = () => resolve({ media: m, width: 0 });
              img.src = url;
            })
        )
      );

      if (!cancelled) {
        setMedias(results.filter(r => r.width >= 600).map(r => r.media));
      }
    };

    checkWidths();
    return () => { cancelled = true; };
  }, [rawMedias]);

  // Gallery state
  const [currentIdx, setCurrentIdx] = useState(0);
  const thumbStripRef = useRef<HTMLDivElement>(null);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Zoom & rotate state (fullscreen only)
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const lastPanOffset = useRef({ x: 0, y: 0 });

  const resetTransform = () => {
    setZoom(1);
    setRotation(0);
    setPanOffset({ x: 0, y: 0 });
    lastPanOffset.current = { x: 0, y: 0 };
  };

  // Reset currentIdx when filtered medias change (avoid out-of-bounds)
  useEffect(() => {
    setCurrentIdx(0);
  }, [medias]);

  // Reset zoom/rotate when switching image
  useEffect(() => {
    resetTransform();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx]);

  useEffect(() => {
    if (!thumbStripRef.current) return;
    const activeThumb = thumbStripRef.current.children[currentIdx] as HTMLElement | undefined;
    activeThumb?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentIdx]);

  // ── Keyboard navigation & Escape for fullscreen ──────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isFullscreen) return;
      if (e.key === 'Escape') { setIsFullscreen(false); resetTransform(); }
      if (e.key === 'ArrowLeft' && zoom === 1) setCurrentIdx(i => (i - 1 + medias.length) % medias.length);
      if (e.key === 'ArrowRight' && zoom === 1) setCurrentIdx(i => (i + 1) % medias.length);
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 4));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 1));
      if (e.key === 'r' || e.key === 'R') setRotation(r => (r + 90) % 360);
      if (e.key === '0') resetTransform();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, medias.length, zoom]);

  // Prevent body scroll when fullscreen is open
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      resetTransform();
    }
    return () => { document.body.style.overflow = ''; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  useEffect(() => {
    if (!isTranslated) return;

    const prevTitle = document.title;
    document.title = translatedTitle;

    // Cập nhật meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') ?? null;
    if (metaDesc && translatedDesc) {
      metaDesc.setAttribute('content', translatedDesc.slice(0, 160));
    }

    // (tùy chọn) Open Graph cho share trong app
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const prevOg = ogTitle?.getAttribute('content') ?? null;
    ogTitle?.setAttribute('content', translatedTitle);

    // Khôi phục khi rời trang (tránh rớt title sang post khác)
    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
      if (ogTitle && prevOg !== null) ogTitle.setAttribute('content', prevOg);
    };
  }, [isTranslated, translatedTitle, translatedDesc]);

  // Interaction state (optimistic, will be overridden by sidebar data)
  const [liked, setLiked] = useState(post.isLiked);
  const [likeCount, setLikeCount] = useState(post.likes);
  const [saved, setSaved] = useState(post.isSaved);

  // Comment input
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Async sidebar state ──────────────────────────────────────────────────────
  const [sidebarLoaded, setSidebarLoaded] = useState(false);
  const [author, setAuthor] = useState<Author | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [related, setRelated] = useState<RelatedItem[]>([]);

  // Dịch title của related posts (chạy lại khi related load xong)
  const { titleMap: relatedTitleMap } = useFeedTranslate(
    related.map(r => ({ id: r.id, title: r.title })),
    locale,
  );

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/posts/${post.id}/sidebar`);
        if (!res.ok) return;
        const data = await res.json();
        setAuthor(data.author);
        setComments(data.comments);
        setRelated(data.related);
        setLiked(data.isLiked);
        setSaved(data.isSaved);
      } catch {
        // Silently fail — non-critical data
      } finally {
        setSidebarLoaded(true);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [post.id]);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const requireAuth = (fn: () => void) => {
    if (!session) { router.push('/login'); return; }
    fn();
  };

  const handleLike = () => requireAuth(async () => {
    const nl = !liked;
    setLiked(nl); setLikeCount(c => nl ? c + 1 : c - 1);
    await fetch(`/api/posts/${post.id}/tick`, { method: nl ? 'POST' : 'DELETE' });
  });

  const handleSave = () => requireAuth(async () => {
    const ns = !saved; setSaved(ns);
    toast.success(ns ? t.post.savedToCollection : t.post.removedFromCollection);
    await fetch('/api/bookmarks', {
      method: ns ? 'POST' : 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ objectId: post.id, objectType: 1 }),
    });
  });

  const countShare = () => { fetch(`/api/posts/${post.id}/share`, { method: 'POST' }).catch(() => {}); };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t.post.linkCopied);
      setShareMenuOpen(false);
      countShare();
    } catch { toast.error(t.common.error); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = viewOriginal ? post.title : translatedTitle;

    // Ưu tiên hộp chia sẻ của trình duyệt/hệ điều hành (điện thoại, Safari, Edge...).
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        countShare();
      } catch (err) {
        if ((err as DOMException)?.name !== 'AbortError') setShareMenuOpen(true);
      }
      return;
    }
    // Trình duyệt không có Web Share (vd. Chrome desktop) → menu chia sẻ mạng xã hội.
    setShareMenuOpen(o => !o);
  };

  const shareTargets = () => {
    const u = encodeURIComponent(window.location.href);
    const ti = encodeURIComponent(viewOriginal ? post.title : translatedTitle);
    return [
      { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
      { name: 'X', href: `https://twitter.com/intent/tweet?url=${u}&text=${ti}` },
      { name: 'Telegram', href: `https://t.me/share/url?url=${u}&text=${ti}` },
      { name: 'WhatsApp', href: `https://wa.me/?text=${ti}%20${u}` },
      { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
      { name: 'Email', href: `mailto:?subject=${ti}&body=${u}` },
    ];
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    requireAuth(async () => {
      setSubmitting(true);
      try {
        const res = await fetch('/api/comments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ objectId: post.id, content: commentText }),
        });
        const newComment = await res.json();
        setComments(c => [newComment, ...c]);
        setCommentText('');
        toast.success(t.post.saved);
      } catch { toast.error(t.common.error); }
      finally { setSubmitting(false); }
    });
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const currentMedia = medias[currentIdx];
  const currentSrc = currentMedia?.src || currentMedia?.thumb || `https://picsum.photos/seed/${post.id}/800/1200`;
  const hashtags = post.keywords || post.hashtags?.split(',').map(h => h.trim()).filter(Boolean) || [];

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const SWIPE_THRESHOLD = 50; // px tối thiểu để tính là swipe

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const delta = touchStartX.current - touchEndX.current;

    if (Math.abs(delta) < SWIPE_THRESHOLD) return; // quá ngắn, bỏ qua

    if (delta > 0) {
      // Vuốt trái → next
      setCurrentIdx(i => (i + 1) % medias.length);
    } else {
      // Vuốt phải → prev
      setCurrentIdx(i => (i - 1 + medias.length) % medias.length);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* ── Left: Gallery + Interactions + Comments ─────────────────────── */}
        <div className="lg:col-span-7">

          {/* Gallery */}
          <div className="relative rounded-3xl overflow-hidden bg-surface-800 group"
            onTouchStart={medias.length > 1 ? handleTouchStart : undefined}
            onTouchEnd={medias.length > 1 ? handleTouchEnd : undefined}
          >
            <Image
              src={currentSrc}
              alt={post.title}
              width={degraded ? 320 : 800}
              height={degraded ? 480 : 1200}
              quality={degraded ? 15 : undefined}
              className={`w-full h-auto max-h-[75vh] object-contain transition-all${degraded ? ' blur-sm saturate-50 pointer-events-none select-none' : ''}`}
              priority
            />

            {/* Fullscreen button */}
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-4 right-4 w-10 h-10 bg-black/60 backdrop-blur-sm rounded-xl flex items-center justify-center text-white hover:bg-black/80 transition opacity-40 group-hover:opacity-100 z-10"
              title="Xem toàn màn hình"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>

            {medias.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentIdx(i => (i - 1 + medias.length) % medias.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white hover:bg-black/80 transition opacity-40 group-hover:opacity-100"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentIdx(i => (i + 1) % medias.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center text-white hover:bg-black/80 transition opacity-40 group-hover:opacity-100"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {medias.map((_, i) => (
                    <button key={i} onClick={() => setCurrentIdx(i)}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentIdx ? 'bg-white w-4' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {medias.length > 1 && (
            <div ref={thumbStripRef} className="flex gap-2 mt-3 px-1 overflow-x-auto scrollbar-hide">
              {medias.map((m, i) => (
                <button key={m.id} onClick={() => setCurrentIdx(i)}
                  className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                    i === currentIdx
                      ? 'border-brand-500 scale-105 shadow-lg shadow-brand-500/30'
                      : 'border-transparent opacity-60 hover:opacity-100 hover:border-surface-500'
                  }`}
                >
                  <Image src={m.thumb || m.src || ''} alt={`${post.title} - image ${i}`} width={64} height={64} quality={degraded ? 10 : undefined} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex gap-3">
            <button onClick={handleLike}
              className={`flex-1 py-3.5 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${liked ? 'bg-brand-600 text-white' : 'bg-gradient-brand text-white hover:brightness-110'}`}>
              <svg className="w-4 h-4" fill={liked ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {liked ? t.post.liked : t.post.like}
            </button>
            <button onClick={handleSave}
              className={`flex-1 py-3.5 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all border ${saved ? 'border-brand-500 text-brand-400' : 'border-surface-600 text-text-primary hover:border-brand-500 hover:text-white'}`}>
              <svg className="w-4 h-4" fill={saved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              {saved ? t.post.saved : t.post.save}
            </button>
            <div className="relative flex-1" ref={shareRef}>
            <button onClick={handleShare} aria-haspopup="menu" aria-expanded={shareMenuOpen}
              className="w-full py-3.5 rounded-2xl font-medium text-sm flex items-center justify-center gap-2 transition-all border border-surface-600 text-text-primary hover:border-brand-500 hover:text-white">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {t.post.share}
            </button>
            {shareMenuOpen && (
              <div role="menu" className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-2xl border border-surface-700 bg-surface-900 py-1 shadow-2xl">
                {shareTargets().map(item => (
                  <a key={item.name} role="menuitem" href={item.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => { setShareMenuOpen(false); countShare(); }}
                    className="flex min-h-[44px] items-center px-4 text-sm text-text-primary hover:bg-surface-800 hover:text-white">
                    {item.name}
                  </a>
                ))}
                <button type="button" role="menuitem" onClick={copyLink}
                  className="flex min-h-[44px] w-full items-center border-t border-surface-700 px-4 text-left text-sm text-text-primary hover:bg-surface-800 hover:text-white">
                  {fp.copyLink}
                </button>
              </div>
            )}
            </div>
          </div>

          {/* Title & stats */}
          <div className="mt-6 flex items-start justify-between gap-4">
            <div>
              {/* Title */}
              <h1 className="font-display text-2xl font-bold text-white leading-tight">
                {viewOriginal ? post.title : translatedTitle}
                {isTranslating && (
                  <span className="ml-2 inline-block w-3 h-3 rounded-full border-2 border-text-muted border-t-white animate-spin align-middle" />
                )}
              </h1>

              {/* Công tắc bản dịch / bản gốc */}
              {isTranslated && (
                <div className="mt-2 flex items-center gap-3 text-xs text-text-muted">
                  <span>🌐 {t.autotrans.title}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={viewOriginal}
                    onClick={() => setShowOriginal(v => !v)}
                    className="inline-flex min-h-[44px] items-center gap-2 text-text-secondary hover:text-white"
                  >
                    <span className={viewOriginal ? 'text-text-muted' : 'font-semibold text-white'}>{fp.showTranslation}</span>
                    <span className={`relative h-5 w-9 rounded-full transition-colors ${viewOriginal ? 'bg-brand-700' : 'bg-surface-600'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${viewOriginal ? 'left-[18px]' : 'left-0.5'}`} />
                    </span>
                    <span className={viewOriginal ? 'font-semibold text-white' : 'text-text-muted'}>{fp.showOriginal}</span>
                  </button>
                </div>
              )}
              <div className="flex items-center gap-4 text-sm text-text-secondary flex-shrink-0">
                <p className="text-text-secondary text-sm mt-1">{timeAgo(post.createdAt)}</p>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {formatNumber(post.views)}
                </span>
                <span className="flex items-center gap-1.5 text-brand-400">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {formatNumber(likeCount)}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          {post.description && (
            <div className="mt-6 bg-surface-900 rounded-2xl p-5">
              <p className="text-text-primary text-base leading-relaxed whitespace-pre-wrap">
                {(isTranslated && !showOriginal && translatedDesc ? translatedDesc : post.description).split(/(#[\w\u00C0-\u024F\u1E00-\u1EFF]+|\n)/g).map((part, i) =>
                  part === '\n' ? <br key={i} /> :
                  part.startsWith('#') ? (
                    <Link key={i} href={`/hashtag/${part.slice(1)}`}
                      className="text-brand-400 hover:text-brand-300 transition-colors">
                      {part}
                    </Link>
                  ) : part
                )}
              </p>
            </div>
          )}

          {/* Hashtags */}
          {hashtags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {hashtags.map(tag => (
                <Link key={tag} href={`/hashtag/${tag}`}
                  className="text-sm text-brand-400 hover:text-brand-300 bg-brand-500/10 hover:bg-brand-500/20 px-3 py-1 rounded-2xl transition-colors">
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Sidebar ad */}
          <div className="flex flex-col items-center gap-1 mt-2 mb-2">
            {/* <AdSlot type="300x250" />
            <p className="text-text-secondary text-[10px]">Ads</p> */}
          </div>

          {/* ── Comments ────────────────────────────────────────────────────── */}
          <div className="mt-10">
            <h3 className="font-display font-bold text-xl mb-6">
              {t.post.comments?.replace('{count}', `${post.commentCount ?? 0}`)}
            </h3>

            {/* Input */}
            <div className="flex gap-3 mb-8">
              <div className="w-9 h-9 rounded-xl bg-gradient-brand flex-shrink-0 flex items-center justify-center text-white text-sm font-bold">
                {session?.user?.name?.[0] || '?'}
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  value={commentText}
                  onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitComment()}
                  placeholder={session ? t.post.commentPlaceholder : 'Đăng nhập để bình luận...'}
                  className="flex-1 bg-surface-800 border border-surface-600 rounded-2xl px-4 py-2.5 text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-brand-500 transition-colors"
                  disabled={!session}
                />
                <button onClick={submitComment} disabled={submitting || !commentText.trim() || !session}
                  className="bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium px-5 rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {t.post.commentSubmit}
                </button>
              </div>
            </div>

            {/* Comment list */}
            <div className="space-y-6">
              {!sidebarLoaded
                ? Array.from({ length: 3 }).map((_, i) => <CommentSkeleton key={i} />)
                : comments.length === 0
                  ? <p className="text-text-secondary text-sm text-center py-8">{t.post.noComments}</p>
                  : comments.map(c => (
                    <div key={c.id} className="flex gap-3">
                      <div className="w-9 h-9 rounded-xl bg-surface-700 flex-shrink-0 flex items-center justify-center text-white text-sm font-bold overflow-hidden">
                        {c.author.avatar
                          ? <img src={c.author.avatar} alt="" className="w-full h-full object-cover" />
                          : c.author.name[0]}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="font-medium text-sm text-white">{c.author.name}</span>
                          <span className="text-text-secondary text-xs">{timeAgo(c.createdAt)}</span>
                        </div>
                        <p className="text-text-primary text-sm mt-1 leading-relaxed">{c.content}</p>
                        {c.replyCount > 0 && (
                          <button className="text-brand-400 text-xs mt-1 hover:text-brand-300">
                            {c.replyCount} {t.post.replies}
                          </button>
                        )}
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>

        {/* ── Right: Author + Ads + Related ───────────────────────────────── */}
        <div className="lg:col-span-5">

          {/* Author card */}
          {!sidebarLoaded
            ? <AuthorSkeleton />
            : author && (
              <div className="bg-surface-900 rounded-2xl p-5 mb-6 border border-white/5">
                <div className="flex items-center gap-4">
                  <Link href={`/u/${author.username || author.id}`}>
                    <div className="w-14 h-14 rounded-2xl bg-gradient-brand flex items-center justify-center text-white text-xl font-bold overflow-hidden">
                      {author.avatar
                        ? <img src={author.avatar} alt={author.name} className="w-full h-full object-cover" />
                        : author.name[0]}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link href={`/u/${author.username || author.id}`}>
                      <p className="font-semibold text-white truncate hover:text-brand-400 transition-colors">{author.name}</p>
                    </Link>
                    <p className="text-text-secondary text-sm">{formatNumber(author.followerCount)} followers • {author.postCount} ảnh</p>
                  </div>
                </div>
                <Link href={`/u/${author.username || author.id}`}
                  className="mt-4 block w-full text-center border border-surface-600 hover:border-brand-500 hover:text-white text-text-primary text-sm font-medium py-2.5 rounded-2xl transition-all">
                  {t.post.profileView}
                </Link>
              </div>
            )
          }

          {/* Sidebar ad */}
          <div className="flex flex-col items-center gap-1 mt-2 mb-2">
            <AdSlot type="300x250-2" />
            <p className="text-text-secondary text-[10px]">Ads</p>
          </div>

          {/* Related posts */}
          <h3 className="font-display font-bold text-xl mb-4">{t.post.relatedPosts}</h3>
          <div className="grid grid-cols-2 gap-3">
            {!sidebarLoaded
              ? <RelatedSkeleton />
              : related.map((r) => (
                <Fragment key={r.id}>
                  <RelatedCard {...r} title={relatedTitleMap.get(r.id) ?? r.title} />
                </Fragment>
              ))
            }
          </div>
          {/* Sidebar ad */}
          <div className="flex flex-col items-center gap-1 mt-2 mb-2">
            <AdSlot type="native2" />
            <p className="text-text-secondary text-[10px]">Ads</p>
          </div>
        </div>
      </div>

      {/* ── Fullscreen Overlay ──────────────────────────────────────────────── */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center overflow-hidden"
          onClick={() => { setIsFullscreen(false); resetTransform(); }}
        >
          {/* ── Top bar ── */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-20 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
            {/* Counter */}
            {medias.length > 1 ? (
              <span className="bg-black/50 text-white text-sm px-4 py-1.5 rounded-full select-none pointer-events-auto">
                {currentIdx + 1} / {medias.length}
              </span>
            ) : <span />}

            {/* Zoom indicator */}
            {zoom !== 1 && (
              <span className="bg-black/50 text-white text-sm px-3 py-1.5 rounded-full select-none">
                {Math.round(zoom * 100)}%
              </span>
            )}

            {/* Close */}
            <button
              onClick={e => { e.stopPropagation(); setIsFullscreen(false); resetTransform(); }}
              className="pointer-events-auto w-10 h-10 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center text-white transition"
              title="Đóng (Esc)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* ── Toolbar ── */}
          <div
            className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-2xl px-2 py-1.5"
            onClick={e => e.stopPropagation()}
          >
            {/* Zoom out */}
            <button
              onClick={() => setZoom(z => Math.max(+(z - 0.25).toFixed(2), 0.5))}
              disabled={zoom <= 0.5}
              className="w-9 h-9 flex items-center justify-center text-white rounded-xl hover:bg-white/10 transition disabled:opacity-30"
              title="Thu nhỏ (-)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
              </svg>
            </button>

            {/* Zoom slider */}
            <input
              type="range" min={50} max={400} step={25}
              value={Math.round(zoom * 100)}
              onChange={e => { setZoom(Number(e.target.value) / 100); setPanOffset({ x: 0, y: 0 }); lastPanOffset.current = { x: 0, y: 0 }; }}
              className="w-24 accent-white cursor-pointer"
              title={`Zoom: ${Math.round(zoom * 100)}%`}
            />

            {/* Zoom in */}
            <button
              onClick={() => setZoom(z => Math.min(+(z + 0.25).toFixed(2), 4))}
              disabled={zoom >= 4}
              className="w-9 h-9 flex items-center justify-center text-white rounded-xl hover:bg-white/10 transition disabled:opacity-30"
              title="Phóng to (+)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>

            <div className="w-px h-5 bg-white/20 mx-1" />

            {/* Rotate CCW */}
            <button
              onClick={() => setRotation(r => (r - 90 + 360) % 360)}
              className="w-9 h-9 flex items-center justify-center text-white rounded-xl hover:bg-white/10 transition"
              title="Xoay trái"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>

            {/* Rotate CW */}
            <button
              onClick={() => setRotation(r => (r + 90) % 360)}
              className="w-9 h-9 flex items-center justify-center text-white rounded-xl hover:bg-white/10 transition"
              title="Xoay phải (R)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              </svg>
            </button>

            <div className="w-px h-5 bg-white/20 mx-1" />

            {/* Reset */}
            <button
              onClick={resetTransform}
              disabled={zoom === 1 && rotation === 0 && panOffset.x === 0 && panOffset.y === 0}
              className="w-9 h-9 flex items-center justify-center text-white rounded-xl hover:bg-white/10 transition disabled:opacity-30"
              title="Đặt lại (0)"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {/* ── Image with pan ── */}
          <div
            className="relative flex items-center justify-center w-full h-full"
            style={{ cursor: zoom > 1 ? (isPanning.current ? 'grabbing' : 'grab') : 'default' }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => {
              if (zoom <= 1) return;
              isPanning.current = true;
              panStart.current = { x: e.clientX - lastPanOffset.current.x, y: e.clientY - lastPanOffset.current.y };
            }}
            onMouseMove={e => {
              if (!isPanning.current) return;
              const nx = e.clientX - panStart.current.x;
              const ny = e.clientY - panStart.current.y;
              setPanOffset({ x: nx, y: ny });
            }}
            onMouseUp={() => {
              if (!isPanning.current) return;
              isPanning.current = false;
              lastPanOffset.current = panOffset;
            }}
            onMouseLeave={() => {
              if (!isPanning.current) return;
              isPanning.current = false;
              lastPanOffset.current = panOffset;
            }}
            onWheel={e => {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.15 : 0.15;
              setZoom(z => Math.min(Math.max(+(z + delta).toFixed(2), 0.5), 4));
              if (e.deltaY > 0 && zoom + (-0.15) <= 1) {
                setPanOffset({ x: 0, y: 0 });
                lastPanOffset.current = { x: 0, y: 0 };
              }
            }}
          >
            <Image
              src={currentSrc}
              alt={post.title}
              width={degraded ? 320 : 800}
              height={degraded ? 480 : 1200}
              quality={degraded ? 15 : undefined}
              className={`w-full h-auto max-h-[75vh] object-contain rounded-xl transition-all${degraded ? ' blur-sm saturate-50 pointer-events-none select-none' : ''}`}
              priority
              draggable={false}
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                transition: isPanning.current ? 'none' : 'transform 0.2s ease',
              }}
            />
          </div>

          {/* ── Prev / Next ── */}
          {medias.length > 1 && zoom === 1 && (
            <>
              <button
                onClick={e => { e.stopPropagation(); setCurrentIdx(i => (i - 1 + medias.length) % medias.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center text-white transition z-10"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={e => { e.stopPropagation(); setCurrentIdx(i => (i + 1) % medias.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center text-white transition z-10"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* ── Bottom: thumbnail strip + hint ── */}
          <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-2 pb-4 bg-gradient-to-t from-black/60 to-transparent pt-8">
            {medias.length > 1 && (
              <div
                className="flex gap-2 px-4 overflow-x-auto max-w-[80vw] scrollbar-hide"
                onClick={e => e.stopPropagation()}
              >
                {medias.map((m, i) => (
                  <button
                    key={m.id}
                    onClick={() => setCurrentIdx(i)}
                    className={`flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                      i === currentIdx
                        ? 'border-white scale-110 shadow-lg shadow-white/20'
                        : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <Image src={m.thumb || m.src || ''} alt={`${post.title} - image ${i}`} width={56} height={56} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <p className="text-white/25 text-xs select-none pointer-events-none">
              {zoom > 1 ? 'Kéo để di chuyển  •  Cuộn để zoom  •  Esc để đóng'
                : medias.length > 1 ? '← → chuyển ảnh  •  Scroll để zoom  •  R xoay  •  Esc đóng'
                : 'Scroll để zoom  •  R xoay  •  Esc đóng'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}